(function () {
  if (window.Manager) {
    return;
  }

  const GROUP_COLOR = 'color:#4875a2';
  const CONSOLE_COLOR = 'color:#bada55';

  class Manager extends HTMLElement {
    constructor() {
      super();
      this.attachedEvents = [];
      this.enforceActions = false;
      this.stateHistory = { begin: {}, changes: [], end: {} };

      // Create reference so uglify will not be able to remove debugging output at build-time.
      this.console = window.console;

      this._bindHandlers();
    }

    connectedCallback() {
      this.connected = true;
      this.traceName = this._getTraceName();

      // Gate tracing on the `traceManager` localStorage key: `*` traces every
      // manager, or a comma-separated list of traceNames traces only those.
      // localStorage is used (not a cookie) so it survives reloads for local
      // debugging without adding request-header bytes (see cookieMonitor / 431).
      let traceFlag = '';
      try {
        traceFlag = window.localStorage.getItem('traceManager') || '';
      } catch (err) {
        traceFlag = '';
      }
      this.traceEnabled = traceFlag === '*' || traceFlag.split(',').map(s => s.trim()).includes(this.traceName);

      if (!this.traceEnabled) {
        return;
      }

      // Snapshot initial state only when tracing, so disabled sessions stay zero-cost.
      this.stateHistory.begin = { ...this.state };

      if (!this.state) {
        this._errorMessage('INITIAL_STATE_UNDEFINED');
      }
      if (typeof this.state !== 'object') {
        this._errorMessage('INITIAL_STATE_TYPE_ERROR');
      }
      if (typeof this.stateUpdated !== 'function') {
        this._errorMessage('STATE_UPDATED_TYPE_ERROR', true);
      }
    }

    disconnectedCallback() {
      this.detachAllEvents();
    }

    attachEvent(els, eventType, fn) {
      if (els instanceof NodeList) {
        els = [...els];
      }
      if (!Array.isArray(els)) {
        els = [els];
      }

      els.forEach((el) => {
        if (!el || !el.addEventListener) {
          return;
        }
        el.addEventListener(eventType, fn);
        this.attachedEvents.push({ el, eventType, fn });
      });
    }

    detachAllEvents() {
      this.attachedEvents.forEach(({ el, eventType, fn }) => el.removeEventListener(eventType, fn));
      this.attachedEvents = [];
    }

    /**
     * Update the manager state and trigger stateUpdated().
     * @param {Object} value - Properties to merge into state
     */
    setState(value) {
      if (this.enforceActions && typeof value.action !== 'string') {
        throw new Error(`${this.constructor.name}: Every state update must define an action string`);
      }

      this.prevState = { ...this.state };
      this.state = { ...this.state, ...value };

      // Only record/emit when tracing is on. Keeps `stateHistory` from growing
      // unbounded in production sessions where nothing consumes it.
      if (this.traceEnabled && !this.silentActions?.includes(this.state?.action)) {
        this._traceAction();

        this.stateHistory.changes.push({
          action: this.state?.action,
          ...this._diff(this.prevState, this.state),
        });
      }

      if (typeof this.stateUpdated === 'function') {
        this.stateUpdated();
      }
    }

    /**
     * Async dispatch helper for state transitions.
     * @param {Object} options
     * @param {Function} options.nextState - Async callback returning state update
     * @param {Object} options.nextAction - { success, fail, abort } action strings
     */
    async dispatch(options = {}) {
      const { nextState, nextAction = {} } = options;

      if (typeof nextState !== 'function') {
        console.error(`${this.constructor.name}: dispatch requires options.nextState to be a function`); // eslint-disable-line no-console
        return;
      }

      let result;
      let resultError = false;

      try {
        result = await nextState();
      } catch (err) {
        resultError = true;
        result = err;
      }

      if (result instanceof Error) {
        resultError = true;
      }

      // Abort: nextState returned undefined
      if (typeof result === 'undefined' && nextAction.abort) {
        return this.setState({ action: nextAction.abort });
      }

      // Fail
      if (resultError && nextAction.fail) {
        const errors = [...(this.state.errors || []), result];
        return this.setState({ action: nextAction.fail, errors });
      }

      // Success with state update
      if (!resultError && result && typeof result === 'object') {
        return this.setState({ ...result, action: nextAction.success });
      }

      // Fallback
      this.setState({ action: nextAction.success || 'unknown' });
    }

    didStateChange(property) {
      return !this._deepEqual(this.prevState?.[property], this.state?.[property]);
    }

    domAction(e, elName) {
      this._onDomAction(e, elName);
    }

    /**
     * Find elements with `[data-{traceName}]` attribute for use by the manager.
     * @param {HTMLElement} [el=document]
     * @returns {Object}
     */
    domElements(el = document) {
      const traceName = this._getTraceName();
      const els = [...el.querySelectorAll(`[data-${traceName}]`)];
      const dom = els.reduce((o, el) => {
        const [elName] = el.dataset[traceName.toLowerCase()].split(':');
        if (!o[elName]) {
          o[elName] = [];
        }
        o[elName].push(el);
        return o;
      }, {});

      if (this.traceEnabled && !this.waitDomTrace) {
        // This method is called twice in succession: to populate `this.dom` and by `domActions()`.
        // Add timeout to prevent duplicate output.
        this.waitDomTrace = true;
        setTimeout(() => {
          this.waitDomTrace = false;
          this._traceItem(`${this.traceName} domElements`, dom);
        }, 10);
      }

      return dom;
    }

    /**
     * Register DOM events from data attributes as state actions.
     * @param {Array} [domRegistry=[]] - Array of `elementName:eventType` strings
     * @param {HTMLElement} [el=document]
     */
    domActions(domRegistry = [], el = document) {
      if (typeof this.state.domEvent !== 'object') {
        this.state.domEvent = {};
      }
      if (typeof this.state.domEventCount !== 'number') {
        this.state.domEventCount = 0;
      }

      const upperFirst = str => `${str[0].toUpperCase()}${str.slice(1)}`;

      // Register events from domRegistry array
      domRegistry.forEach((str) => {
        const [elName, eventTypes] = str.split(':');
        const el = this.dom[elName];
        if (!eventTypes || !el) {
          return;
        }
        const els = typeof el[Symbol.iterator] === 'function' ? [...el] : [el];
        els.forEach((el) => {
          eventTypes.split(',').forEach((eventType) => {
            el.addEventListener(eventType, (e) => {
              const domEventCount = this.state.domEventCount + 1;
              const action = `on${upperFirst(elName)}${upperFirst(eventType)}`;
              this.setState({ action, domEvent: e, domEventCount });
            });
          });
        });
      });

      // Register events from data attributes
      const traceName = this._getTraceName();
      const dom = this.domElements(el);
      Object.keys(dom).forEach((elName) => {
        dom[elName].forEach((el) => {
          const attrValue = el.dataset[traceName.toLowerCase()];
          const [, eventTypes] = attrValue.split(':');
          if (!eventTypes) {
            return;
          }

          eventTypes.split(',').forEach((eventType) => {
            el.addEventListener(eventType, this._onDomAction);
            this.attachedEvents.push({ el, eventType, fn: this._onDomAction });
          });
        });
      });
    }

    /**
     * Re-scan the DOM for `[data-{traceName}]` elements, detach previously
     * tracked listeners, and re-register domActions. Use after DOM
     * replacement (e.g. innerHTML swap) to rebind without leaking listeners.
     */
    domRefresh() {
      this.detachAllEvents();
      this.dom = { ...this.dom, ...this.domElements() };
      this.domActions();
    }

    /**
     * Resolve once all given custom element tags are defined.
     * @param {string|Array} [tags=[]]
     * @returns {Promise<void>}
     */
    async elementsDefined(tags = []) {
      if (!Array.isArray(tags)) {
        tags = [tags];
      }

      try {
        await Promise.all(tags.map(tag => window.customElements.whenDefined(tag)));
      } catch (err) {
        console.error(err); // eslint-disable-line no-console
      }
    }

    _bindHandlers() {
      this._onDomAction = this._onDomAction.bind(this);

      const startsWithAny = (checkStr, arr) => arr.some(str => checkStr.startsWith(str));

      Object.getOwnPropertyNames(this.constructor.prototype)
        .filter(prop => startsWithAny(prop, ['_on', '_dispatch', '_fetch', '_render']))
        .forEach(handler => (this[handler] = this[handler].bind(this)));
    }

    _onDomAction(e, elName) {
      const traceName = this._getTraceName();
      elName = elName || e.currentTarget.dataset[traceName.toLowerCase()].split(':')[0];
      const domEventCount = this.state.domEventCount + 1;
      const upperFirst = str => `${str[0].toUpperCase()}${str.slice(1)}`;
      const action = `on${upperFirst(elName)}${upperFirst(e.type)}`;
      this.setState({ action, domEvent: e, domEventCount });
    }

    _getTraceName() {
      if (this.traceName) {
        return this.traceName;
      }
      // Convert ClassName to kebab-case
      return this.constructor.name
        .replace(/([a-z])([A-Z])/g, '$1-$2')
        .toLowerCase();
    }

    _deepEqual(prev, curr) {
      return JSON.stringify(prev) === JSON.stringify(curr);
    }

    /**
     * Returns the keys/values in `compare` that differ from `control`.
     * Recurses into nested objects; removed keys map to `undefined`.
     * @param {*} control
     * @param {*} compare
     * @returns {Object}
     */
    _diff(control, compare) {
      if (control === compare) {
        return {};
      }

      const isObject = o => o !== null && typeof o === 'object';
      if (!isObject(control) || !isObject(compare)) {
        return compare;
      }

      /* eslint-disable no-prototype-builtins */
      const compareRemoved = Object.keys(control).reduce(
        (o, key) => (compare.hasOwnProperty(key) ? o : { ...o, [key]: undefined }),
        {}
      );

      return Object.keys(compare).reduce((o, key) => {
        if (!control.hasOwnProperty(key)) {
          return { ...o, [key]: compare[key] }; // Added.
        }

        const changes = this._diff(control[key], compare[key]); // Recursion.

        if (isObject(changes) && Object.keys(changes).length === 0) {
          return o; // No changes in recursed.
        }

        return { ...o, [key]: changes }; // Changed.
      }, compareRemoved);
      /* eslint-enable no-prototype-builtins */
    }

    _errorMessage(type, isThrow) {
      let message = {
        ENFORCE_ACTION: 'Every state update must define an `action` string: `this.state.action`',
        INITIAL_STATE_UNDEFINED: '`this.state` should be initialized in constructor()',
        INITIAL_STATE_TYPE_ERROR: '`this.state` must be an object.',
        STATE_KEY_INVALID: 'Invalid state key provided.',
        STATE_UPDATED_TYPE_ERROR: '`this.stateUpdated` must be a function.',
      }[type] || type;
      message = `${this._getTraceName()}: ${message}`;

      if (isThrow) {
        throw new Error(message);
      }
      this.console.warn(message); // eslint-disable-line no-console
    }

    _traceAction() {
      /* eslint-disable no-console */
      if (!this.traceEnabled) {
        return;
      }
      if (!this.enforceActions) {
        return;
      }

      this.console.log(`%c${this.traceName} action: ${this.state.action} - ${Date.now() / 1000}`, CONSOLE_COLOR);

      this.console.groupCollapsed(`%c${this.traceName} state:`, GROUP_COLOR);
      this.console.log('%cSTATE:', CONSOLE_COLOR, this.state);
      this.console.groupEnd();

      const beforeDiff = this._diff(this.state, this.prevState);
      const afterDiff = this._diff(this.prevState, this.state);
      this.console.groupCollapsed(`%c${this.traceName} diff:`, GROUP_COLOR);
      this.console.log('%cPREV:', CONSOLE_COLOR, beforeDiff);
      this.console.log('%cNEXT:', CONSOLE_COLOR, afterDiff);
      this.console.groupEnd();
      /* eslint-enable no-console */
    }

    _traceItem(key, value) {
      this.console.log(`%c${key}`, CONSOLE_COLOR, value);
    }
  }

  window.Manager = Manager;

  /* istanbul ignore next */
  if (typeof module !== 'undefined') {
    module.exports = Manager;
  }
})();
